import React, { useContext, useState, useEffect } from "react";
import { GlobalContext } from "GlobalContext";
import Reveal from "ui/Reveal";
import {
  NewsletterWrapper,
  Intro,
  Eyebrow,
  Label,
  Form,
  Field,
  MailIcon,
  Input,
  Button,
  Spinner,
  Status,
  Success,
  Check,
} from "./styles/NewsletterStyles";

const MESSAGES = {
  fr: {
    eyebrow: "Newsletter",
    confirmed: "Merci ! Vous êtes inscrit(e) à notre newsletter.",
    pending:
      "Merci ! Confirmez votre inscription via l’e-mail que nous venons de vous envoyer.",
    exists: "Cette adresse est déjà inscrite à notre newsletter.",
    invalid_email: "Merci de saisir une adresse e-mail valide.",
    rate_limited: "Trop de tentatives. Merci de réessayer dans un moment.",
    error: "Une erreur est survenue, merci de réessayer.",
  },
  en: {
    eyebrow: "Newsletter",
    confirmed: "Thank you! You are now subscribed to our newsletter.",
    pending: "Thank you! Please confirm through the email we just sent you.",
    exists: "This address is already subscribed to our newsletter.",
    invalid_email: "Please enter a valid email address.",
    rate_limited: "Too many attempts. Please try again in a moment.",
    error: "Something went wrong, please try again.",
  },
};

// Posts to /newsletter/subscribe on this same domain: nginx hands it to the
// WordPress endpoint (mu-plugin ia-newsletter.php), which subscribes through
// the Newsletter plugin. No API key lives in this bundle any more.
const Newsletter = () => {
  const { newsletterData, currentLang } = useContext(GlobalContext);
  const { description, libelleBouton, placeholder, error } = newsletterData;
  const isEn = currentLang === "en";
  const t = MESSAGES[isEn ? "en" : "fr"];

  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | confirmed | pending | error
  const [errorKey, setErrorKey] = useState("");

  useEffect(() => {
    error && console.log("Newsletter data loading error: ", error.message);
  }, [error]);

  const subscribe = async (e) => {
    e.preventDefault();
    setState("sending");
    setErrorKey("");
    try {
      const response = await fetch(
        (isEn ? "/en" : "") + "/newsletter/subscribe",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setState(data.status === "pending" ? "pending" : "confirmed");
        return;
      }
      setErrorKey(t[data.code] ? data.code : "error");
    } catch (err) {
      setErrorKey("error");
    }
    setState("error");
  };

  if (!placeholder || !libelleBouton) return null;

  const done = state === "confirmed" || state === "pending";

  return (
    <Reveal className="newsletter-reveal">
      <NewsletterWrapper>
        <Intro>
          <Eyebrow>{t.eyebrow}</Eyebrow>
          {description && (
            <Label dangerouslySetInnerHTML={{ __html: description }} />
          )}
        </Intro>
        {done ? (
          <Success role="status">
            <Check viewBox="0 0 24 24" aria-hidden="true">
              <path d="M5 12.5l4.5 4.5L19 7.5" />
            </Check>
            <span>{t[state]}</span>
          </Success>
        ) : (
          <Form onSubmit={subscribe}>
            <Field className={state === "error" ? "has-error" : ""}>
              <MailIcon viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="M3.5 6.5l8.5 6.5 8.5-6.5" />
              </MailIcon>
              <Input
                type="email"
                required
                autoComplete="email"
                aria-label={placeholder}
                placeholder={placeholder}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (state === "error") setState("idle");
                }}
              />
              <Button type="submit" disabled={state === "sending"}>
                {libelleBouton.charAt(0).toUpperCase() + libelleBouton.slice(1)}
                {state === "sending" ? (
                  <Spinner aria-hidden="true" />
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                )}
              </Button>
            </Field>
            <Status role="status" aria-live="polite">
              {state === "error" && t[errorKey]}
            </Status>
          </Form>
        )}
      </NewsletterWrapper>
    </Reveal>
  );
};

export default Newsletter;
